import { Box, Card, Typography, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form, required, useCheckAuth, useLogin, useNotify, useTranslate } from 'ra-core';
import { useEffect, useState } from 'react';
import { PasswordInput, TextInput } from 'react-admin';
import { useNavigate } from 'react-router-dom';

export const Login = () => {
    const theme = useTheme();
    const checkAuth = useCheckAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const login = useLogin();
    const translate = useTranslate();
    const notify = useNotify();

    useEffect(() => {
        checkAuth({}, false).then(() => {
            navigate('/');
        });
    }, [checkAuth, navigate]);

    const submit = (values: any) => {
        setLoading(true);

        login(values)
            .then(() => {
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                notify(translate('errorMessages.incorrect_password'), {
                    type: 'error',
                });
            });
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `radial-gradient(circle at 20% 50%, rgba(63, 81, 181, 0.3) 0%, transparent 50%),
                                 radial-gradient(circle at 80% 80%, rgba(34, 38, 61, 0.3) 0%, transparent 50%)`,
                    pointerEvents: 'none',
                },
            }}
        >
            <Card
                sx={{
                    width: '100%',
                    maxWidth: 420,
                    p: 4,
                    mx: 2,
                    position: 'relative',
                    zIndex: 1,
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(10px)',
                    border: `1px solid rgba(255, 255, 255, 0.3)`,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        mb: 4,
                    }}
                >
                    <Box
                        component='img'
                        src='/images/big-logo.png'
                        alt='BIG Logo'
                        sx={{
                            height: 60,
                            mb: 3,
                            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
                        }}
                    />
                    <Typography
                        variant='h5'
                        component='h1'
                        sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            mb: 0.5,
                        }}
                    >
                        {translate('ra.auth.sign_in')}
                    </Typography>
                </Box>

                <Form onSubmit={submit}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2.5,
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: 'white',
                                    },
                                },
                                '& .RaTextInput-input': {
                                    width: '100%',
                                },
                            }}
                        >
                            <TextInput
                                autoFocus
                                source='username'
                                label={translate('main.navigation.email')}
                                validate={required()}
                                helperText={false}
                                fullWidth
                            />
                        </Box>

                        <Box
                            sx={{
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: 'white',
                                    },
                                },
                                '& .RaPasswordInput-input': {
                                    width: '100%',
                                },
                            }}
                        >
                            <PasswordInput
                                source='password'
                                label={translate('ra.auth.password')}
                                validate={required()}
                                helperText={false}
                                fullWidth
                            />
                        </Box>

                        <LoadingButton
                            variant='contained'
                            type='submit'
                            color='primary'
                            fullWidth
                            loading={loading}
                            sx={{
                                mt: 1,
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontSize: '1rem',
                                fontWeight: 600,
                                boxShadow: '0 4px 12px rgba(63, 81, 181, 0.3)',
                                '&:hover:not(:disabled)': {
                                    boxShadow: '0 6px 16px rgba(63, 81, 181, 0.4)',
                                    transform: 'translateY(-1px)',
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            {translate('ra.auth.sign_in')}
                        </LoadingButton>
                    </Box>
                </Form>
            </Card>
        </Box>
    );
};

export default Login;
